import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IHome } from "@miplatform/types";
import { createUUID, flattenHome } from "@miplatform/utils";

const getHomesUUID = createUUID();
const removeHomeUUID = createUUID();

export const useCommunityHomesTabViewModel = (communityId: string) => {
  const stateManager = useComponentState();
  const [homes, setHomes] = useState<IHome[]>([]);

  const getHomesTiedToCommunity = () => {
    if (!communityId) {
      setHomes([]);
      return;
    }

    stateManager.abort(getHomesUUID);
    stateManager.run(async () => {
      const response = await homeService.homes.getAll({
        filters: {
          communityId: {
            type: "guid",
            value: communityId,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setHomes(response.data.map((home: IHome) => flattenHome(home)));
      }
    }, getHomesUUID);
  };

  const linkHomeToCommunity = async (home: IHome) => {
    stateManager.abort(getHomesUUID);
    stateManager.run(async () => {
      if (communityId) {
        const response = await homeService.homes.update({
          id: home.id,
          data: {
            ...home,
            communityId,
          },
          toastMessageConfig: {
            success: {
              text: "Home successfully added to community.",
            },
            failure: {
              text: "Failed to add home to community.",
            },
          },
        });

        if (response.status === HTTP_STATUSES.OK) {
          getHomesTiedToCommunity();
        }
      }
    }, getHomesUUID);
  };

  const removeHomeFromCommunity = async (home: IHome) => {
    stateManager.abort(removeHomeUUID);
    stateManager.run(async () => {
      const response = await homeService.homes.update({
        id: home.id,
        data: {
          ...home,
          communityId: null,
        },
        toastMessageConfig: {
          success: {
            text: "Home successfully removed from community.",
          },
          failure: {
            text: "Failed to remove home from community.",
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        getHomesTiedToCommunity();
      }
    }, removeHomeUUID);
  };

  return {
    homes,
    getHomesTiedToCommunity,
    removeHomeFromCommunity,
    linkHomeToCommunity,
    isLoading: stateManager.isBusy(),
  };
};
